import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './WorkshopReports.css';

function WorkshopReports() {
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date();
    d.setDate(1);
    return d.toISOString().split('T')[0];
  });
  const [dateTo, setDateTo] = useState(() => {
    return new Date().toISOString().split('T')[0];
  });
  const [utilization, setUtilization] = useState([]);
  const [revenue, setRevenue] = useState([]);
  const [manifestDate, setManifestDate] = useState(new Date().toISOString().split('T')[0]);
  const [manifestLocation, setManifestLocation] = useState('');
  const [manifest, setManifest] = useState(null);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [loadingManifest, setLoadingManifest] = useState(false);

  useEffect(() => {
    fetchLocations();
  }, []);

  useEffect(() => {
    if (dateFrom && dateTo) {
      fetchReports();
    }
  }, [dateFrom, dateTo]);

  const fetchLocations = async () => {
    try {
      const response = await axios.get('/api/workshops/locations');
      setLocations(response.data || []);
    } catch (err) {
      console.error('Failed to fetch locations:', err);
    }
  };

  const fetchReports = async () => {
    try {
      setLoading(true);
      const params = { startDate: dateFrom, endDate: dateTo };

      const [utilRes, revRes] = await Promise.all([
        axios.get('/api/workshops/reports/utilization', { params }),
        axios.get('/api/workshops/reports/revenue', { params })
      ]);

      setUtilization(utilRes.data || []);
      setRevenue(revRes.data || []);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch reports:', err);
      setError('Failed to load report data');
    } finally {
      setLoading(false);
    }
  };

  const fetchManifest = async () => {
    if (!manifestDate) return;
    try {
      setLoadingManifest(true);
      const params = {};
      if (manifestLocation) params.location = manifestLocation;
      const response = await axios.get(`/api/workshops/reports/manifest/${manifestDate}`, { params });
      setManifest(response.data);
    } catch (err) {
      console.error('Failed to fetch manifest:', err);
      setManifest(null);
    } finally {
      setLoadingManifest(false);
    }
  };

  const formatPrice = (cents) => {
    if (!cents && cents !== 0) return '$0.00';
    return `$${(cents / 100).toFixed(2)}`;
  };

  const maxUtil = Math.max(...utilization.map((u) => u.utilization_percent || 0), 1);

  if (loading && utilization.length === 0) {
    return (
      <div className="workshop-reports">
        <div className="ws-loading">
          <div className="spinner"></div>
          <p>Loading reports...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="workshop-reports">
      <div className="ws-page-header">
        <h1>Workshop Reports</h1>
      </div>

      <div className="ws-filters">
        <div className="ws-filter-group">
          <label>From</label>
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
        </div>
        <div className="ws-filter-group">
          <label>To</label>
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
        </div>
      </div>

      {error && <div className="ws-error">{error}</div>}

      <div className="ws-section">
        <h2>Utilization by Location</h2>
        {utilization.length === 0 ? (
          <div className="ws-empty">No utilization data for selected period</div>
        ) : (
          <div className="ws-util-chart">
            {utilization.map((item) => (
              <div key={item.location_name || item.location_id} className="ws-util-row">
                <span className="ws-util-label">{item.location_name}</span>
                <div className="ws-util-bar-container">
                  <div
                    className="ws-util-bar"
                    style={{
                      width: `${(item.utilization_percent / maxUtil) * 100}%`,
                      backgroundColor: '#8B6F4E'
                    }}
                  >
                    <span className="ws-util-value">{item.utilization_percent}%</span>
                  </div>
                </div>
                <span className="ws-util-detail">
                  {item.booked_slots}/{item.total_slots} slots
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="ws-section">
        <h2>Revenue Summary</h2>
        {revenue.length === 0 ? (
          <div className="ws-empty">No revenue data for selected period</div>
        ) : (
          <div className="ws-table-container">
            <table className="ws-table">
              <thead>
                <tr>
                  <th>Location</th>
                  <th>Sessions</th>
                  <th>Total Bookings</th>
                  <th>Total Attendees</th>
                  <th>Revenue</th>
                </tr>
              </thead>
              <tbody>
                {revenue.map((item) => (
                  <tr key={item.location_name || item.location_id}>
                    <td className="ws-text-bold">{item.location_name}</td>
                    <td>{item.session_count}</td>
                    <td>{item.booking_count}</td>
                    <td>{item.total_attendees}</td>
                    <td className="ws-text-bold">{formatPrice(item.total_revenue)}</td>
                  </tr>
                ))}
                <tr className="ws-total-row">
                  <td className="ws-text-bold">Total</td>
                  <td>{revenue.reduce((s, r) => s + (r.session_count || 0), 0)}</td>
                  <td>{revenue.reduce((s, r) => s + (r.booking_count || 0), 0)}</td>
                  <td>{revenue.reduce((s, r) => s + (r.total_attendees || 0), 0)}</td>
                  <td className="ws-text-bold">
                    {formatPrice(revenue.reduce((s, r) => s + (r.total_revenue || 0), 0))}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="ws-section">
        <h2>Daily Manifest</h2>
        <div className="ws-manifest-controls">
          <div className="ws-filter-group">
            <label>Date</label>
            <input
              type="date"
              value={manifestDate}
              onChange={(e) => setManifestDate(e.target.value)}
            />
          </div>
          <div className="ws-filter-group">
            <label>Location</label>
            <select
              value={manifestLocation}
              onChange={(e) => setManifestLocation(e.target.value)}
            >
              <option value="">All Locations</option>
              {locations.map((loc) => (
                <option key={loc.id} value={loc.slug}>{loc.name}</option>
              ))}
            </select>
          </div>
          <button
            className="ws-btn-primary"
            onClick={fetchManifest}
            disabled={loadingManifest}
          >
            {loadingManifest ? 'Loading...' : 'View Manifest'}
          </button>
        </div>

        {manifest && (
          <div className="ws-manifest-results">
            {(manifest.sessions || []).length === 0 ? (
              <div className="ws-empty">No sessions found for this date</div>
            ) : (
              (manifest.sessions || []).map((session) => (
                <div key={session.id} className="ws-manifest-session">
                  <div className="ws-manifest-session-header">
                    <span className="ws-text-bold">
                      {session.workshop_type_name} - {session.location_name}
                    </span>
                    <span>{session.start_time} - {session.end_time}</span>
                  </div>
                  {(session.bookings || []).length === 0 ? (
                    <div className="ws-empty-sm">No bookings</div>
                  ) : (
                    <table className="ws-table ws-manifest-table">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Email</th>
                          <th>Phone</th>
                          <th>Attendees</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {session.bookings.map((booking) => (
                          <tr key={booking.id}>
                            <td className="ws-text-bold">{booking.customer_name}</td>
                            <td>{booking.customer_email}</td>
                            <td>{booking.customer_phone || '--'}</td>
                            <td>{booking.attendee_count || 1}</td>
                            <td>
                              <span className={`ws-badge ws-badge-${booking.status}`}>
                                {booking.status}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default WorkshopReports;
